import { Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Logger } from 'common/utility/logger/logger';
import { merge, Subject } from 'rxjs';
import { debounceTime, filter, take, takeUntil } from 'rxjs/operators';
import { Battery } from 'services/battery/battery';
import { NotificationService } from 'services/notification/notification.service';
import { BatteryStringsService } from 'src/app/services/battery-strings/battery-strings.service';

const DEBOUNCE_MS = 1000;
const AUTO_SAVE_DEBOUNCE_MS = 3000;

@Component({
  selector: 'ems-battery-strings',
  templateUrl: './battery-strings.html',
  styleUrls: ['./battery-strings.scss']
})
export class BatteryStrings implements OnInit, OnChanges, OnDestroy {
  @Input() id: string = '';
  @Input() label: string = '';
  @Input() showAddButton: boolean = false;
  @ViewChild('moduleInputEl') moduleInputEl!: ElementRef<HTMLInputElement>;
  @ViewChild('batteryInputEl') batteryInputEl?: ElementRef<HTMLInputElement>;

  get Title(): string {
    return this.label || this.id || 'Battery Strings';
  }

  batteryControl = new FormControl<string | null>(null);
  BatteryOptions: Battery[] = [];
  adding: boolean = false;
  saving: boolean = false;

  form: FormGroup = this.fb.group({
    referenceDesignator: [null, [Validators.maxLength(5)]],
    serialNumber:        [null, [Validators.maxLength(13)]],
    cuSerialNumber:      [null, [Validators.maxLength(22)]],
    fsmSerialNumber:     [null, [Validators.maxLength(23)]],
    fsBmsSwRevision:     [null, [Validators.maxLength(15)]],
    parametersVersion:   [null, [Validators.maxLength(30)]],
  });

  moduleInput = new FormControl<string | null>(null, [Validators.maxLength(11)]);
  moduleList: string[] = [];

  private readonly moduleChanges$ = new Subject<void>();
  private readonly destroy$ = new Subject<void>();
  private pendingSave: boolean = false;

  get CanAddModule(): boolean {
    const value = this.moduleInput.value?.trim();
    return !this.moduleInput.disabled && !!value && this.moduleList.length < 32 && !this.moduleList.includes(value);
  }

  get batteryIdExists(): boolean {
    return this.adding && !!this.batteryControl.value
      && this.BatteryOptions.some(option => option.batteryId === this.batteryControl.value);
  }

  get moduleSerialDuplicate(): boolean {
    const value = this.moduleInput.value?.trim();
    return !!value && this.moduleList.includes(value);
  }

  addModule(): void {
    const val = this.moduleInput.value?.trim();
    if (val && this.moduleList.length < 32) {
      this.moduleList.push(val);
      this.moduleInput.reset();
      this.moduleChanges$.next();
      this.moduleInputEl.nativeElement.focus();
    }
  }

  removeModule(index: number): void {
    this.moduleList.splice(index, 1);
    this.moduleChanges$.next();
    this.moduleInputEl.nativeElement.focus();
  }

  add(): void {
    this.batteryControl.reset();
    this.form.reset();
    this.moduleList = [];
    this.adding = true;
    this.updateFormState();
    setTimeout(() => this.batteryInputEl?.nativeElement.focus());
  }

  save(): void {
    const batteryId = this.batteryControl.value;
    if (!batteryId) return;

    const battery: Battery = this.buildBattery(batteryId);

    this.saving = true;
    this.batteryStringsService.saveBattery(batteryId, battery).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: () => {
        this.saving = false;
        this.adding = false;
        this.BatteryOptions = [...this.BatteryOptions, battery];
        this.batteryControl.setValue(batteryId);
        this.notificationService.notify({ message: `saved ${batteryId}` });
      },
      error: (err) => {
        this.saving = false;
        Logger.error(`Failed to save battery ${batteryId}: ${JSON.stringify(err)}`);
        this.notificationService.notify({ message: `Error saving ${batteryId}`, notificationType: 'error' });
      }
    });
  }

  ngOnInit(): void {
    this.batteryControl.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(batteryId => {
      this.updateFormState();
      this.populateForm(batteryId);
    });
    this.updateFormState();

    const formChanges$ = merge(this.form.valueChanges, this.moduleChanges$).pipe(
      filter(() => !this.adding && !!this.batteryControl.value),
      takeUntil(this.destroy$)
    );

    formChanges$.subscribe(() => {
      this.pendingSave = true;
    });

    formChanges$.pipe(
      debounceTime(AUTO_SAVE_DEBOUNCE_MS),
      filter(() => this.form.valid)
    ).subscribe(() => this.autoSave());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['id'] && this.id) {
      if (this.pendingSave) {
        this.autoSave();
      }
      this.adding = false;
      this.BatteryOptions = [];
      this.moduleList = [];
      this.batteryControl.reset();
      this.form.reset();
      this.moduleInput.reset();
      this.loadBatteries();
      this.updateFormState();
    }
  }

  ngOnDestroy(): void {
    if (this.pendingSave) {
      this.autoSave();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  constructor(
    private fb: FormBuilder,
    private batteryStringsService: BatteryStringsService,
    private notificationService: NotificationService
  ) {}

  private updateFormState(): void {
    const enabled = !!this.batteryControl.value;
    enabled ? this.form.enable({ emitEvent: false }) : this.form.disable({ emitEvent: false });
    enabled ? this.moduleInput.enable({ emitEvent: false }) : this.moduleInput.disable({ emitEvent: false });
  }

  private populateForm(batteryId: string | null): void {
    if (!batteryId) return;
    const battery = this.BatteryOptions.find(option => option.batteryId === batteryId);
    if (!battery) return;

    this.form.patchValue({
      referenceDesignator: battery.referenceDesignator,
      serialNumber: battery.serialNumber,
      cuSerialNumber: battery.cuSerialNumber,
      fsmSerialNumber: battery.fsmSerialNumber,
      fsBmsSwRevision: battery.fsBmsSwRevision,
      parametersVersion: battery.parametersVersion,
    }, { emitEvent: false });
    this.moduleList = [...(battery.moduleSerialNumbers ?? [])];
  }

  private buildBattery(batteryId: string): Battery {
    const v = this.form.value;
    return {
      batteryId: batteryId,
      edgeId: this.id,
      referenceDesignator: v.referenceDesignator ?? '',
      serialNumber: v.serialNumber ?? '',
      cuSerialNumber: v.cuSerialNumber ?? '',
      fsmSerialNumber: v.fsmSerialNumber ?? '',
      fsBmsSwRevision: v.fsBmsSwRevision ?? '',
      parametersVersion: v.parametersVersion ?? '',
      moduleSerialNumbers: [...this.moduleList]
    };
  }

  private autoSave(): void {
    const batteryId = this.batteryControl.value;
    if (!batteryId) return;

    this.pendingSave = false;
    const battery: Battery = this.buildBattery(batteryId);

    this.batteryStringsService.saveBattery(batteryId, battery).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: () => {
        this.notificationService.notify({ message: `Battery ${batteryId} saved` });
      },
      error: (err) => {
        Logger.error(`Failed to update battery ${batteryId}: ${JSON.stringify(err)}`);
        this.notificationService.notify({ message: `Error saving ${batteryId}`, notificationType: 'error' });
      }
    });
  }

  private loadBatteries(): void {
    this.batteryStringsService.getBatteries(this.id).pipe(
      take(1),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (batteries: Battery[]) => {
        this.BatteryOptions = batteries;
        if (batteries.length) {
          this.batteryControl.setValue(batteries[0].batteryId);
        }
      },
      error: (err) => Logger.error(`Failed to load batteries for ${this.id}: ${JSON.stringify(err)}`)
    });
  }

}
